import type { ApiQuestion, ApiQuestionSetting } from '../../../../internalTypes';
import { QUESTION_CONFIG_KEY } from '../../../../constants/questionConfigKeys';
import type { ContainerMedia } from '../../../../types/shared';
import { shouldShowQuestionHint } from '../../../../../surveyLogic/fields/questionHintUtils';
import { getConfigFlag } from './configUtils';

/** Internal mapper output — not exported to SDK clients. */
export type ResolvedQuestionShell = {
  id: string;
  questionText: string;
  questionDescription?: string;
  questionHint?: string;
  required: boolean;
  requiredErrorMessage?: string;
  questionNumber?: number;
  containerMedia?: ContainerMedia;
  referencedQuestionIds?: string[];
};

export function resolveQuestionShell(
  raw: ApiQuestion,
  configs: ApiQuestionSetting[],
  language?: string,
  placeholders?: Record<string, string>
): ResolvedQuestionShell {
  const required = getConfigFlag(configs, QUESTION_CONFIG_KEY.REQUIRED);

  const reqErrConfig = configs.find(c => c.key === QUESTION_CONFIG_KEY.REQUIRED_ERROR_MESSAGE);
  let requiredErrorMessage = reqErrConfig?.value?.[0];
  if (language && reqErrConfig?.translations?.[language]?.value?.[0]) {
    requiredErrorMessage = reqErrConfig.translations[language].value![0];
  }

  let questionText =
    (language && raw.translations?.[language]?.questionRichText) ||
    (language && raw.translations?.[language]?.questionText) ||
    raw.questionRichText ||
    raw.questionText ||
    '';

  if (placeholders) {
    questionText = questionText.replace(/\{\{(\w+)\}\}/g, (match, key) =>
      placeholders[key] !== undefined ? placeholders[key] : match
    );
  }

  let questionDescription =
    (language && raw.translations?.[language]?.descriptionRichText) ||
    (language && raw.translations?.[language]?.description) ||
    raw.descriptionRichText ||
    raw.description ||
    raw.descriptionText ||
    (typeof raw.additional?.descriptionRichText === 'string' ? raw.additional.descriptionRichText : '') ||
    (typeof raw.additional?.description === 'string' ? raw.additional.description : '') ||
    '';

  if (!questionDescription) {
    const descConfig = configs.find(c => c.key.includes('DESCRIPTION'));
    questionDescription = descConfig?.value?.[0] || '';
    if (language && descConfig?.translations?.[language]?.valueRichText?.[0]) {
      questionDescription = descConfig.translations[language].valueRichText![0];
    } else if (language && descConfig?.translations?.[language]?.value?.[0]) {
      questionDescription = descConfig.translations[language].value![0];
    }
  }

  if (questionDescription && placeholders) {
    questionDescription = questionDescription.replace(/\{\{(\w+)\}\}/g, (match, key) =>
      placeholders[key] !== undefined ? placeholders[key] : match
    );
  }

  let questionHint = raw.questionHint;
  const hintConfig = configs.find(c => c.key === 'QUESTION_HINT');
  if (hintConfig?.value?.[0] && hintConfig.value[0] !== 'true' && hintConfig.value[0] !== 'false') {
    questionHint = hintConfig.value[0];
  }

  const containerAssets = raw.questionContainerAssets;
  const primaryAsset = containerAssets?.[0];
  const url = primaryAsset?.source || primaryAsset?.mediaUrl || primaryAsset?.previewImageUrl;
  const mimeType = primaryAsset?.mediaMimeType;

  const alignConfig = configs.find(c => c.key === 'QUESTION_CONTAINER_MEDIA_ALIGNMENT');
  const alignment = alignConfig?.value?.[0];

  const sizeConfig = configs.find(c => c.key === 'TEXT_AND_MEDIA_SIZE');
  const size = sizeConfig?.value?.[0] ? Number(sizeConfig.value[0]) : undefined;

  const containerMedia: ContainerMedia | undefined =
    url || mimeType || alignment || size !== undefined
      ? {
          ...(url ? { url } : {}),
          ...(mimeType ? { mimeType } : {}),
          ...(alignment ? { alignment } : {}),
          ...(size !== undefined ? { size } : {}),
        }
      : undefined;

  const associatedIds = raw.additionalInfo?.associatedPlaceholderQuestionIds;
  const referencedQuestionIds = Array.isArray(associatedIds)
    ? associatedIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : undefined;

  return {
    id: raw.id ?? '',
    questionText,
    ...(questionDescription ? { questionDescription } : {}),
    ...(shouldShowQuestionHint(questionHint) ? { questionHint } : {}),
    required,
    ...(requiredErrorMessage ? { requiredErrorMessage } : {}),
    ...(raw.questionNumber !== undefined ? { questionNumber: raw.questionNumber } : {}),
    ...(containerMedia ? { containerMedia } : {}),
    ...(referencedQuestionIds && referencedQuestionIds.length > 0
      ? { referencedQuestionIds }
      : {}),
  };
}
